import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  Image,
  LayoutGrid,
  List,
  GripVertical,
  Eye,
  ChefHat,
} from "lucide-react";
import api, {
  getCodes,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getAllPresetsAPI,
} from "../../services/api";
import PrerequisitesDialog from "../../components/PrerequisitesDialog";
import { toast } from "react-hot-toast";
import AddMenuDialog from "../../components/AddMenuDailog";
import { getTokenInfo } from "../../utils/auth";

interface CategoryCode {
  _id: string;
  code: string;
  name: string;
  taxable: boolean;
}

interface Category {
  _id: string;
  name: string;
  imageUrl?: string;
  categoryCode?: CategoryCode;
  taxable?: boolean;
  /** Modifier sets added to every menu item in this category */
  presetIds?: string[];
}

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  discount?: number;
  category: string;
  categoryId: string;
  categoryCode?: string;
  imageUrl: string;
  description?: string;
  tags?: string[];
  customize?: any[];
  foodType: "veg" | "non-veg" | "both";
  fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[];
  kitchenName?: string;
  prepTime?: number;
}

interface Code {
  _id: string;
  code: string;
  name: string;
  taxable: boolean;
}


const CategoryManagement: React.FC = () => {
  // Location admins can only view items and toggle per-location settings
  const isMasterAdmin = getTokenInfo()?.role === "master_admin";
  const [categories, setCategories] = useState<Category[]>([]);
  const [codes, setCodes] = useState<Code[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // View mode state
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  // Menu items modal state
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuViewMode, setMenuViewMode] = useState<"grid" | "list">("list");
  const [menuDraggedItem, setMenuDraggedItem] = useState<number | null>(null);
  const [menuDragOverItem, setMenuDragOverItem] = useState<number | null>(null);
  const [isReorderingMenu, setIsReorderingMenu] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState("");

  // Add menu item dialog state
  const [showAddMenuDialog, setShowAddMenuDialog] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);

  // Form fields
  const [formName, setFormName] = useState("");
  const [formImageUrl, setFormImageUrl] = useState("");
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [taxable, setTaxable] = useState<boolean>(true);
  // Category modifiers: picked sets are added to every item in the category
  const [presets, setPresets] = useState<any[]>([]);
  const [categoryPresetIds, setCategoryPresetIds] = useState<string[]>([]);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  useEffect(() => {
    getAllPresetsAPI()
      .then((res) => setPresets(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setPresets([]));
  }, []);
  const [selectedCodeId, setSelectedCodeId] = useState<string>("");

  // Image upload
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (): Promise<string> => {
    if (!file) return formImageUrl;
    const formDataObj = new FormData();
    formDataObj.append("image", file);
    setUploading(true);

    try {
      const res = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploading(false);
      setFile(null);
      return res.data.url;
    } catch (err) {
      setUploading(false);
      console.error("Image upload failed:", err);
      return "";
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCategories();
      setCategories(res.data.data);
    } catch (err) {
      setError("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async () => {
    try {
      const res = await getCodes();
      setCodes(res.data);
    } catch (err) {
      setError("Failed to load codes.");
    }
  };

  // Fetch menu items for a category
  const fetchMenuItems = async (categoryId: string) => {
    setMenuLoading(true);
    try {
      const response = await api.get(`/menu/category/${categoryId}`);
      if (response.data.success) {
        setMenuItems(response.data.data.menuItems);
      } else {
        toast.error("Failed to load menu items");
        setMenuItems([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch menu items:", err);
      toast.error(err.response?.data?.message || "Failed to load menu items");
      setMenuItems([]);
    } finally {
      setMenuLoading(false);
    }
  };

  // Reorder categories API call
  const reorderCategories = async (fromIndex: number, toIndex: number) => {
    setIsReordering(true);
    try {
      const response = await api.put("/categories/reorder", {
        fromIndex,
        toIndex,
      });

      if (response.data.success) {
        setCategories(response.data.data.categories);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Failed to reorder categories");
      }
    } catch (err: any) {
      console.error("Reorder failed:", err);
      toast.error(
        err.response?.data?.message || "Failed to reorder categories",
      );
      fetchCategories();
    } finally {
      setIsReordering(false);
    }
  };

  // Reorder menu items API call
  const reorderMenuItems = async (fromIndex: number, toIndex: number) => {
    if (!selectedCategory) return;

    setIsReorderingMenu(true);
    try {
      const response = await api.put("/menu/reorder", {
        categoryId: selectedCategory._id,
        fromIndex,
        toIndex,
      });

      if (response.data.success) {
        setMenuItems(response.data.data.menuItems);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Failed to reorder menu items");
      }
    } catch (err: any) {
      console.error("Menu reorder failed:", err);
      toast.error(
        err.response?.data?.message || "Failed to reorder menu items",
      );
      // Revert by fetching fresh data
      if (selectedCategory) {
        fetchMenuItems(selectedCategory._id);
      }
    } finally {
      setIsReorderingMenu(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCodes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);

    try {
      const imageUrl = await uploadImage();
      const categoryData = {
        name: formName,
        imageUrl: imageUrl || "",
        taxable: taxable,
        categoryCode: selectedCodeId,
        presetIds: categoryPresetIds,
      };

      if (editingCategory) {
        const res = await updateCategory(editingCategory._id, categoryData);
        const changed = Number(res.data?.menuItemsUpdated) || 0;
        toast.success(
          changed > 0
            ? `Category updated — modifiers applied to ${changed} menu item${changed === 1 ? "" : "s"}`
            : "Category updated successfully!",
        );
      } else {
        await createCategory(categoryData);
        toast.success("Category added successfully!");
      }

      resetForm();
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      setError("Failed to save category.");
      toast.error("Failed to save category.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (cat: Category) => {
    setEditingCategory(cat);
    setFormName(cat.name);
    setFormImageUrl(cat.imageUrl || "");
    setSelectedCodeId(cat.categoryCode?._id || "");
    setTaxable(cat.taxable !== undefined ? cat.taxable : true);
    setCategoryPresetIds(cat.presetIds || []);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;
    setError(null);
    try {
      await deleteCategory(id);
      fetchCategories();
      toast.success("Category deleted successfully!");
    } catch (err) {
      setError("Failed to delete category.");
      toast.error("Failed to delete category.");
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormName("");
    setFormImageUrl("");
    setSelectedCodeId("");
    setTaxable(true);
    setCategoryPresetIds([]);
    setFile(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Open menu items modal
  const openMenuModal = (category: Category) => {
    setSelectedCategory(category);
    setShowMenuModal(true);
    setMenuSearchTerm("");
    fetchMenuItems(category._id);
  };

  const closeMenuModal = () => {
    setShowMenuModal(false);
    setSelectedCategory(null);
    setMenuItems([]);
    setMenuSearchTerm("");
  };

  // Delete menu item
  const handleDeleteMenuItem = async (itemId: string) => {
    if (!window.confirm("Are you sure you want to delete this menu item?"))
      return;

    try {
      const response = await api.delete(`/menu/${itemId}`);
      if (response.data.success) {
        toast.success("Menu item deleted successfully!");
        if (selectedCategory) {
          fetchMenuItems(selectedCategory._id);
        }
      } else {
        toast.error(response.data.message || "Failed to delete menu item");
      }
    } catch (err: any) {
      console.error("Failed to delete menu item:", err);
      toast.error(err.response?.data?.message || "Failed to delete menu item");
    }
  };

  // Edit menu item
  const handleEditMenuItem = (item: MenuItem) => {
    setEditingMenuItem(item);
    setShowAddMenuDialog(true);
  };

  // Category drag handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverItem(index);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    reorderCategories(draggedItem, dropIndex);

    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Menu items drag handlers
  const handleMenuDragStart = (e: React.DragEvent, index: number) => {
    setMenuDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleMenuDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setMenuDragOverItem(index);
  };

  const handleMenuDragLeave = () => {
    setMenuDragOverItem(null);
  };

  const handleMenuDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (menuDraggedItem === null || menuDraggedItem === dropIndex) {
      setMenuDraggedItem(null);
      setMenuDragOverItem(null);
      return;
    }

    reorderMenuItems(menuDraggedItem, dropIndex);

    setMenuDraggedItem(null);
    setMenuDragOverItem(null);
  };

  // Filter and pagination logic
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCategories = filteredCategories.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Filter menu items
  const filteredMenuItems = menuItems.filter((item) =>
    item.name.toLowerCase().includes(menuSearchTerm.toLowerCase()),
  );

  // Get food type badge
  const getFoodTypeBadge = (foodType: string) => {
    switch (foodType) {
      case "veg":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Veg
          </span>
        );
      case "non-veg":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Non-Veg
          </span>
        );
      case "both":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Both
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col h-screen overflow-y-auto no-scrollbar">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-row justify-between items-center sm:gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Categories</h1>

          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Grid View"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>

            {/* Add Button */}
            {isMasterAdmin && (
              <button
                onClick={openAddModal}
                className="px-2.5 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base whitespace-nowrap"
              >
                <Plus size={16} />
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add Category</span>
              </button>
            )}
          </div>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Categories
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by category name..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchCategories}
              className="w-10 h-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Categories"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Reordering status */}
        {isReordering && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-700 text-sm">
              Updating category order...
            </span>
          </div>
        )}
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedCategories.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No categories found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : isMasterAdmin
                    ? "Add your first category to get started"
                    : "No categories have been added yet"}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedCategories.map((category) => (
                  <div
                    key={category._id}
                    className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    {/* Image */}
                    <div
                      className="aspect-video bg-gray-200 relative overflow-hidden cursor-pointer"
                      onClick={() => openMenuModal(category)}
                    >
                      {category.imageUrl ? (
                        <img
                          src={category.imageUrl}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image size={32} />
                        </div>
                      )}

                      {/* View Menu Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                        <div className="bg-white rounded-full p-2 transform scale-75 hover:scale-100 transition-transform">
                          <Eye size={20} className="text-blue-600" />
                        </div>
                      </div>

                      {/* Tax Status Badge */}
                      <div className="absolute top-2 right-2">
                        {category.taxable ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Taxable
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Non-Taxable
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="text-xl font-bold text-gray-800">
                        {category.name}
                      </h3>

                      {/* Code Assignment */}
                      <p className="text-sm text-gray-600 mb-3">
                        Code:{" "}
                        <span className="font-medium">
                          {category.categoryCode?.name || "No code assigned"}
                        </span>
                      </p>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openMenuModal(category)}
                          className="flex-1 min-w-0 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded text-sm whitespace-nowrap hover:bg-green-700 transition"
                        >
                          <ChefHat size={14} />
                          Menu Items
                        </button>

                        {isMasterAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(category)}
                              className="w-11 h-11 flex items-center justify-center bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                              title="Edit Category"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(category._id)}
                              className="w-11 h-11 flex items-center justify-center bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                              title="Delete Category"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === "list" && (
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {filteredCategories.map((category, index) => (
                    <div
                      key={category._id}
                      draggable={isMasterAdmin && !isReordering}
                      onDragStart={(e) =>
                        handleDragStart(e, startIndex + index)
                      }
                      onDragOver={(e) => handleDragOver(e, startIndex + index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, startIndex + index)}
                      className={`flex flex-wrap items-start gap-3 p-4 hover:bg-gray-50 transition-colors sm:items-center sm:gap-4 ${
                        draggedItem === startIndex + index ? "opacity-50" : ""
                      } ${
                        dragOverItem === startIndex + index
                          ? "bg-blue-50 border-blue-200"
                          : ""
                      }`}
                    >
                      {/* Drag Handle */}
                      {isMasterAdmin && (
                        <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                          <GripVertical size={20} />
                        </div>
                      )}

                      {/* Image */}
                      <div
                        className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-gray-200 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() => openMenuModal(category)}
                      >
                        {category.imageUrl ? (
                          <img
                            src={category.imageUrl}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Image size={20} />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-[160px]">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-base font-bold text-gray-800 sm:text-lg">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Code:{" "}
                              <span className="font-medium">
                                {category.categoryCode?.name || "No code assigned"}
                              </span>
                            </p>
                          </div>

                          {/* Actions + Tax Status Badge */}
                          <div className="ml-auto flex items-center gap-2 self-end sm:ml-4 sm:self-auto">
                            {category.taxable ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Taxable
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Non-Taxable
                              </span>
                            )}
                            <button
                              onClick={() => openMenuModal(category)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-green-600 transition hover:bg-green-100"
                              title="View Menu Items"
                            >
                              <ChefHat size={16} />
                            </button>
                            {isMasterAdmin && (
                              <>
                                <button
                                  onClick={() => handleEdit(category)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-blue-600 transition hover:bg-blue-100"
                                  title="Edit Category"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(category._id)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-100"
                                  title="Delete Category"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {viewMode === "grid" &&
        filteredCategories.length > 0 &&
        totalPages > 1 && (
          <div className="pt-4 flex justify-center border-t">
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded transition ${
                    currentPage === i + 1
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}

      {/* ================= MENU ITEMS MODAL ================= */}
      {showMenuModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeMenuModal}
          />

          <div className="relative flex max-h-[100dvh] w-full flex-col bg-white shadow-xl sm:max-h-[90vh] sm:max-w-6xl sm:rounded-lg">
            {/* Modal Header */}
            <div className="flex flex-shrink-0 flex-col gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:rounded-t-lg sm:px-6 sm:py-4">
              <div className="flex min-w-0 items-center gap-3">
                <h2 className="truncate text-base font-bold text-gray-800 sm:text-xl">
                  Menu Items - {selectedCategory?.name}
                </h2>
                {isReorderingMenu && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-xs sm:text-sm">Updating order...</span>
                  </div>
                )}
              </div>

              <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-3">
                {/* Menu View Toggle */}
                {isMasterAdmin && (
                  <button
                    onClick={() => setShowAddMenuDialog(true)}
                    className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white transition hover:bg-blue-700"
                    title="Add Menu Items"
                  >
                    <Plus size={14} />
                    Add Item
                  </button>
                )}

                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setMenuViewMode("grid")}
                    className={`p-2 rounded-md transition-all ${
                      menuViewMode === "grid"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    title="Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    onClick={() => setMenuViewMode("list")}
                    className={`p-2 rounded-md transition-all ${
                      menuViewMode === "list"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    title="List View"
                  >
                    <List size={16} />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={closeMenuModal}
                  className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Menu Search */}
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  value={menuSearchTerm}
                  onChange={(e) => setMenuSearchTerm(e.target.value)}
                  placeholder="Search menu items..."
                  className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {menuLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredMenuItems.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <ChefHat size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">No menu items found</p>
                    <p className="text-sm mt-1">
                      {menuSearchTerm
                        ? "Try searching with a different term"
                        : "This category doesn't have any menu items yet"}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Grid View for Menu Items */}
                  {menuViewMode === "grid" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredMenuItems.map((item) => (
                        <div
                          key={item._id}
                          className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                        >
                          <div className="aspect-video bg-gray-200 relative overflow-hidden">
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-2 right-2">
                              {getFoodTypeBadge(item.foodType)}
                            </div>
                            {item.discount && (
                              <div className="absolute top-2 left-2">
                                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  {item.discount}% OFF
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="p-4">
                            <h4 className="text-xl font-bold text-gray-800">
                              {item.name}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {item.description}
                            </p>
                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {item.tags.length > 3 && (
                                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                    +{item.tags.length - 3}
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center gap-2">
                                {item.discount ? (
                                  <>
                                    <span className="text-lg font-bold text-green-600">
                                      $
                                      {(
                                        item.price *
                                        (1 - item.discount / 100)
                                      ).toFixed(0)}
                                    </span>
                                    <span className="text-sm text-gray-500 line-through">
                                      ${item.price}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-lg font-bold text-gray-900">
                                    ${item.price}
                                  </span>
                                )}
                              </div>

                              {/* Edit/Delete Actions */}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleEditMenuItem(item)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                  title={isMasterAdmin ? "Edit Menu Item" : "View Menu Item"}
                                >
                                  {isMasterAdmin ? <Edit size={16} /> : <Eye size={16} />}
                                </button>
                                {isMasterAdmin && (
                                  <button
                                    onClick={() => handleDeleteMenuItem(item._id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                    title="Delete Menu Item"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>
                            </div>

                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* List View for Menu Items */}
                  {menuViewMode === "list" && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="divide-y divide-gray-200">
                        {filteredMenuItems.map((item, index) => (
                          <div
                            key={item._id}
                            draggable={isMasterAdmin && !isReorderingMenu}
                            onDragStart={(e) => handleMenuDragStart(e, index)}
                            onDragOver={(e) => handleMenuDragOver(e, index)}
                            onDragLeave={handleMenuDragLeave}
                            onDrop={(e) => handleMenuDrop(e, index)}
                            className={`flex flex-col gap-3 p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-4 sm:p-4 ${
                              menuDraggedItem === index ? "opacity-50" : ""
                            } ${
                              menuDragOverItem === index
                                ? "bg-blue-50 border-blue-200"
                                : ""
                            }`}
                          >
                            {/* Drag Handle */}
                            {isMasterAdmin && (
                              <div className="hidden flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing sm:block">
                                <GripVertical size={20} />
                              </div>
                            )}

                            {/* Image */}
                            <div className="h-20 w-full flex-shrink-0 overflow-hidden rounded-lg bg-gray-200 sm:w-20">
                              <img
                                src={item.imageUrl}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="flex-1">
                                  <h4 className="text-xl font-bold text-gray-800">
                                    {item.name}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                    {item.description}
                                  </p>

                                  {item.tags && item.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {item.tags
                                        .slice(0, 5)
                                        .map((tag, index) => (
                                          <span
                                            key={index}
                                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                      {item.tags.length > 5 && (
                                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                          +{item.tags.length - 5}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="text-left sm:ml-4 sm:text-right">
                                  <div className="flex items-center gap-2 mb-2">
                                    {getFoodTypeBadge(item.foodType)}
                                    {item.discount && (
                                      <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                        {item.discount}% OFF
                                      </span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {item.discount ? (
                                      <>
                                        <span className="text-lg font-bold text-green-600">
                                          $
                                          {(
                                            item.price *
                                            (1 - item.discount / 100)
                                          ).toFixed(0)}
                                        </span>
                                        <span className="text-sm text-gray-500 line-through">
                                          ${item.price}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="text-lg font-bold text-gray-900">
                                        ${item.price}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="ml-0 flex w-full flex-shrink-0 gap-2 sm:ml-4 sm:w-auto">
                              <button
                                onClick={() => handleEditMenuItem(item)}
                                className="flex-1 rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 sm:flex-none"
                                title={isMasterAdmin ? "Edit Menu Item" : "View Menu Item"}
                              >
                                {isMasterAdmin ? <Edit size={16} /> : <Eye size={16} />}
                              </button>
                              {isMasterAdmin && (
                                <button
                                  onClick={() => handleDeleteMenuItem(item._id)}
                                  className="flex-1 rounded-lg p-2 text-red-600 transition hover:bg-red-50 sm:flex-none"
                                  title="Delete Menu Item"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>

                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex flex-shrink-0 flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:rounded-b-lg sm:px-6 sm:py-4">
              <div className="text-sm text-gray-600">
                Total: {filteredMenuItems.length} menu items
              </div>
              <button
                onClick={closeMenuModal}
                className="w-full rounded-md bg-gray-200 px-4 py-2 text-gray-700 transition hover:bg-gray-300 sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD/EDIT CATEGORY MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">
                {editingCategory ? "Edit Category" : "Add New Category"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form id="category-form" onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Enter category name"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Tax Status *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="taxable"
                          value="true"
                          checked={taxable === true}
                          onChange={() => setTaxable(true)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Taxable</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="taxable"
                          value="false"
                          checked={taxable === false}
                          onChange={() => setTaxable(false)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Non-Taxable
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Category Modifiers
                    </label>
                    <p className="m-0 mb-2 text-xs text-gray-500">
                      Added to every menu item in this category. You can still remove one from a single
                      item — it won't be added back.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {categoryPresetIds.length === 0 ? (
                        <span className="text-xs text-gray-400">No modifiers</span>
                      ) : (
                        categoryPresetIds.map((id) => {
                          const preset = presets.find((p) => p._id === id);
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700"
                            >
                              {preset?.name || "Modifier"}
                              {preset?.hotelName ? ` (${preset.hotelName} only)` : ""}
                              <button
                                type="button"
                                onClick={() =>
                                  setCategoryPresetIds((prev) => prev.filter((x) => x !== id))
                                }
                                className="ml-0.5 text-blue-500 hover:text-blue-800"
                                aria-label="Remove modifier"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPresetPicker(true)}
                      className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50"
                    >
                      + Choose Modifiers
                    </button>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Assign Code
                    </label>
                    <select
                      value={selectedCodeId}
                      onChange={(e) => setSelectedCodeId(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select a code (optional)</option>
                      {codes.map((code) => (
                        <option key={code._id} value={code._id}>
                          {code.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Category Image
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) =>
                          setFile(e.target.files ? e.target.files[0] : null)
                        }
                        className="w-full p-2.5 border-2 border-dashed border-gray-300 rounded-md focus:border-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                      />
                    </div>
                    {uploading && (
                      <p className="text-blue-600 text-sm mt-2 flex items-center">
                        <span className="animate-spin mr-2">⏳</span>
                        Uploading image...
                      </p>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 py-4 rounded-b-lg flex justify-between gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 sm:px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="category-form"
                className="px-4 sm:px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={formLoading || uploading}
              >
                {formLoading || uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {uploading ? "Uploading..." : "Saving..."}
                  </span>
                ) : editingCategory ? (
                  "Update Category"
                ) : (
                  "Add Category"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD/EDIT MENU ITEM DIALOG ================= */}
      <AddMenuDialog
        isOpen={showAddMenuDialog}
        onClose={() => {
          setShowAddMenuDialog(false);
          setEditingMenuItem(null);
        }}
        categoryId={selectedCategory?._id || ""}
        categoryName={selectedCategory?.name || ""}
        categoryCodeId={selectedCategory?.categoryCode?._id || ""}
        categoryCodes={codes}
        onMenuItemAdded={() => {
          if (selectedCategory) {
            fetchMenuItems(selectedCategory._id);
          }
        }}
        editingItem={editingMenuItem}
      />
      <PrerequisitesDialog
        open={showPresetPicker}
        onOpenChange={setShowPresetPicker}
        data={presets}
        selectedIds={categoryPresetIds}
        onSave={(ids) => {
          setCategoryPresetIds(ids);
          setShowPresetPicker(false);
        }}
      />
    </div>
  );
};

export default CategoryManagement;
